// Kontaktsektionen i "Ny kund": personen som ska läggas upp tillsammans med
// kunden, eller läggas till på en befintlig kund.
import React from 'react';
import {
  Box, Typography, TextField, Alert, Radio, RadioGroup, FormControlLabel,
} from '@mui/material';
import MergeChoices from './MergeChoices';

export const EMPTY_NEW_CONTACT = {
  name: '', role: '', department: '', email: '', mobile: '', phone: '', linkedin: '',
};

/**
 * @param {object}   value       kontaktformuläret
 * @param {Function} onChange    tar en uppdaterarfunktion, som setState
 * @param {Function} mark        (key, value) => extra TextField-props för AI-markering
 * @param {object}   dupContact  befintlig kontakt med samma e-post, eller null
 * @param {string}   dupChoice   'update' | 'new'
 * @param {Function} onDupChoice
 * @param {Array}    plan        mergePlan() mot dupContact
 * @param {object}   choices
 * @param {Function} onChoose
 */
export default function ContactFieldsSection({
  value, onChange, mark, dupContact, dupChoice, onDupChoice, plan, choices, onChoose,
}) {
  const set = key => e => onChange(f => ({ ...f, [key]: e.target.value }));
  const field = (key, label, extra = {}) => (
    <TextField size="small" label={label} value={value[key] || ''} onChange={set(key)}
      sx={{ flex: '1 1 200px' }} {...extra} {...mark(key, value[key])} />
  );
  const hasAny = Object.values(value).some(v => String(v || '').trim());
  const missingName = hasAny && !value.name.trim();

  return (
    <Box sx={{ bgcolor: '#fdf6ff', borderRadius: 2, p: 2 }}>
      <Typography variant="caption" fontWeight="bold" color="#6A1B9A"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
        Kontaktperson (valfritt)
      </Typography>

      {dupContact && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {dupContact.name || dupContact.email} finns redan som kontakt med e-post {dupContact.email}.
          </Typography>
          <RadioGroup row value={dupChoice} onChange={e => onDupChoice(e.target.value)}>
            <FormControlLabel value="update" control={<Radio size="small" />}
              label={<Typography variant="body2">Uppdatera befintlig kontakt</Typography>} />
            <FormControlLabel value="new" control={<Radio size="small" />}
              label={<Typography variant="body2">Skapa ny kontakt ändå</Typography>} />
          </RadioGroup>
          {dupChoice === 'update' && (
            <Box sx={{ mt: 1 }}>
              <MergeChoices rows={plan} choices={choices} prefix="contact." onChoose={onChoose} />
            </Box>
          )}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {field('name', 'Namn', missingName ? { error: true, helperText: 'Namn krävs för att kontakten ska sparas' } : {})}
        {field('role', 'Roll / Befattning')}
        {field('department', 'Avdelning')}
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1.5 }}>
        {field('email', 'E-post', { type: 'email', placeholder: 'namn@foretaget.se' })}
        {field('mobile', 'Mobil', { placeholder: '+46 70 123 45 67' })}
        {field('phone', 'Direktnummer', { placeholder: '+46 8 123 456 78' })}
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1.5 }}>
        {field('linkedin', 'LinkedIn-profil', { placeholder: 'https://www.linkedin.com/in/...' })}
      </Box>
    </Box>
  );
}

/** Kontakten är tom (sparas inte) eller komplett nog att spara. */
export function contactIsSavable(c) {
  const hasAny = Object.values(c).some(v => String(v || '').trim());
  return !hasAny || !!c.name.trim();
}

export function contactHasData(c) {
  return Object.values(c).some(v => String(v || '').trim());
}
